import { Host, h } from "@stencil/core";
import CornerType from "./CornerType";
import { getMovementFromEvent } from "../../utilities/mouseUtilities";
import { dataURLtoFile } from "../../utilities/fileUtilities";
/**
 * Allows cropping an image in-browser with the option to enforce a specific final size.
 * All computation happens in the browser and the final image is emmited
 * in an event that has a data-url of the image.
 */
export class DnnImageCropper {
    constructor() {
        /** Sets the output quality of the cropped image (number between 0 and 1). */
        this.quality = 0.8;
        /** When set to true, prevents cropping an image smaller than the required size, which would blow pixel and make the final picture look blurry. */
        this.preventUndersized = false;
        this.focused = false;
        this.defaultResx = {
            capture: "Capture",
            dragAndDropFile: "Drag and drop an image",
            or: "or",
            takePicture: "Take a picture",
            uploadFile: "Upload an image",
            imageTooSmall: "The image you are attempting to upload does not meet the minimum size requirement of {width} pixels by {height} pixels. Please upload a larger image.",
            modalCloseText: "Close",
        };
        this.initializeCropBox = () => {
            const imageRect = this.image.getBoundingClientRect();
            const wantedRatio = (this.width == undefined || this.height == undefined) ? NaN : this.width / this.height;
            // Calculate initial dimensions of the crop box.
            let newWidth, newHeight;
            // Determine if the width or the height is the limiting dimension.
            if (imageRect.width / imageRect.height > wantedRatio) {
                // Image is wider than the wanted ratio, so height is the limiting factor.
                newHeight = imageRect.height;
                newWidth = newHeight * wantedRatio;
            }
            else {
                // Image is taller than the wanted ratio, so width is the limiting factor.
                newWidth = imageRect.width;
                newHeight = newWidth / wantedRatio;
            }
            // Calculate initial position of the crop box.
            const newTop = (imageRect.height - newHeight) / 2;
            const newLeft = (imageRect.width - newWidth) / 2;
            // Apply initial dimensions and position.
            this.crop.style.top = `${Math.round(newTop)}px`;
            this.crop.style.left = `${Math.round(newLeft)}px`;
            this.crop.style.width = `${Math.round(newWidth)}px`;
            this.crop.style.height = `${Math.round(newHeight)}px`;
        };
        this.handleCropMouseDown = (event) => {
            event.stopPropagation();
            event.preventDefault();
            const element = event.target;
            const className = element.classList[0];
            document.addEventListener("mouseup", this.handleImageCropFinished, false);
            document.addEventListener("touchend", this.handleImageCropFinished, false);
            switch (className) {
                case "crop":
                    document.addEventListener("mousemove", this.handleCropDrag, false);
                    document.addEventListener("touchmove", this.handleCropDrag, false);
                    document.addEventListener("mouseup", () => document.removeEventListener("mousemove", this.handleCropDrag));
                    document.addEventListener("touchend", () => document.removeEventListener("touchmove", this.handleCropDrag));
                    break;
                case "nw":
                    document.addEventListener("mousemove", this.handleNwMouseMove, false);
                    document.addEventListener("touchmove", this.handleNwMouseMove, false);
                    document.addEventListener("mouseup", () => document.removeEventListener("mousemove", this.handleNwMouseMove));
                    document.addEventListener("touchend", () => document.removeEventListener("touchmove", this.handleNwMouseMove));
                    break;
                case "ne":
                    document.addEventListener("mousemove", this.handleNeMouseMove, false);
                    document.addEventListener("touchmove", this.handleNeMouseMove, false);
                    document.addEventListener("mouseup", () => document.removeEventListener("mousemove", this.handleNeMouseMove));
                    document.addEventListener("touchend", () => document.removeEventListener("touchmove", this.handleNeMouseMove));
                    break;
                case "se":
                    document.addEventListener("mousemove", this.handleSeMouseMove, false);
                    document.addEventListener("touchmove", this.handleSeMouseMove, false);
                    document.addEventListener("mouseup", () => document.removeEventListener("mousemove", this.handleSeMouseMove));
                    document.addEventListener("touchend", () => document.removeEventListener("touchmove", this.handleSeMouseMove));
                    break;
                case "sw":
                    document.addEventListener("mousemove", this.handleSwMouseMove, false);
                    document.addEventListener("touchmove", this.handleSwMouseMove, false);
                    document.addEventListener("mouseup", () => document.removeEventListener("mousemove", this.handleSwMouseMove));
                    document.addEventListener("touchend", () => document.removeEventListener("touchmove", this.handleSwMouseMove));
                    break;
                default:
                    break;
            }
        };
        this.handleImageCropFinished = (_ev) => {
            this.emitImage();
            document.removeEventListener("mouseup", this.handleImageCropFinished);
            this.previousTouch = undefined;
        };
        this.handleNwMouseMove = (event) => {
            this.handleCornerDrag(event, CornerType.nw);
        };
        this.handleNeMouseMove = (event) => {
            this.handleCornerDrag(event, CornerType.ne);
        };
        this.handleSeMouseMove = (event) => {
            this.handleCornerDrag(event, CornerType.se);
        };
        this.handleSwMouseMove = (event) => {
            this.handleCornerDrag(event, CornerType.sw);
        };
        this.handleCornerDrag = (event, corner) => {
            const wantedRatio = (this.width == undefined || this.height == undefined) ? NaN : this.width / this.height;
            const cropRect = this.crop.getBoundingClientRect();
            const imageRect = this.image.getBoundingClientRect();
            let { movementX, movementY } = getMovementFromEvent(event, this.previousTouch);
            let newWidth, newHeight;
            switch (corner) {
                case CornerType.nw:
                    newWidth = cropRect.width - movementX;
                    newHeight = cropRect.height - movementY;
                    break;
                case CornerType.ne:
                    newWidth = cropRect.width + movementX;
                    newHeight = cropRect.height - movementY;
                    break;
                case CornerType.se:
                    newWidth = cropRect.width + movementX;
                    newHeight = cropRect.height + movementY;
                    break;
                case CornerType.sw:
                    newWidth = cropRect.width - movementX;
                    newHeight = cropRect.height + movementY;
            }
            let newTop, newLeft;
            if (!isNaN(wantedRatio)) {
                switch (corner) {
                    case CornerType.nw:
                        newHeight = newWidth / wantedRatio;
                        newTop = cropRect.bottom - newHeight;
                        break;
                    case CornerType.se:
                        newWidth = newHeight * wantedRatio;
                        break;
                    case CornerType.ne:
                        newHeight = newWidth / wantedRatio;
                        newTop = cropRect.bottom - newHeight;
                        break;
                    case CornerType.sw:
                        newWidth = newHeight * wantedRatio;
                        newLeft = cropRect.right - newWidth;
                        break;
                }
            }
            switch (corner) {
                case CornerType.nw:
                    newTop = this.crop.offsetTop + (cropRect.height - newHeight);
                    newLeft = this.crop.offsetLeft + (cropRect.width - newWidth);
                    break;
                case CornerType.ne:
                    newTop = this.crop.offsetTop + (cropRect.height - newHeight);
                    newLeft = this.crop.offsetLeft;
                    break;
                case CornerType.se:
                    newTop = this.crop.offsetTop;
                    newLeft = this.crop.offsetLeft;
                    break;
                case CornerType.sw:
                    newTop = this.crop.offsetTop;
                    newLeft = this.crop.offsetLeft + (cropRect.width - newWidth);
                    break;
            }
            if (newLeft < 0) {
                newWidth += newLeft;
                newLeft = 0;
            }
            if (newTop < 0) {
                newHeight += newTop;
                newTop = 0;
            }
            if (newLeft + newWidth > imageRect.width) {
                newWidth = imageRect.width - newLeft;
            }
            if (newTop + newHeight > imageRect.height) {
                newHeight = imageRect.height - newTop;
            }
            // After the boundary checks, recalculate the width and height based on the ratio
            if (!isNaN(wantedRatio)) {
                switch (corner) {
                    case CornerType.se:
                    case CornerType.sw:
                        newWidth = newHeight * wantedRatio;
                        if (newLeft + newWidth > imageRect.width) {
                            newWidth = imageRect.width - newLeft;
                            // Recalculate the height after adjusting the width
                            newHeight = newWidth / wantedRatio;
                        }
                        break;
                }
            }
            if (this.preventUndersized) {
                const zoomRatio = this.image.width / this.image.naturalWidth;
                if (newWidth / zoomRatio < (this.width || NaN) || newHeight / zoomRatio < (this.height || NaN)) {
                    return;
                }
            }
            this.crop.style.top = `${newTop}px`;
            this.crop.style.left = `${newLeft}px`;
            this.crop.style.width = `${newWidth}px`;
            this.crop.style.height = `${newHeight}px`;
        };
        this.handleCropDrag = (ev) => {
            if (!this.isMouseStillInTarget(ev)) {
                return;
            }
            let { movementX, movementY } = getMovementFromEvent(ev, this.previousTouch);
            let newLeft = this.crop.offsetLeft + movementX;
            let newTop = this.crop.offsetTop + movementY;
            var imageRect = this.image.getBoundingClientRect();
            var cropRect = this.crop.getBoundingClientRect();
            if (newLeft < 0) {
                newLeft = 0;
            }
            if (newTop < 0) {
                newTop = 0;
            }
            if (newLeft + cropRect.width > imageRect.width) {
                newLeft = this.crop.offsetLeft;
            }
            if (newTop + cropRect.height > imageRect.height) {
                newTop = this.crop.offsetTop;
            }
            this.crop.style.left = newLeft + "px";
            this.crop.style.top = newTop + "px";
        };
    }
    /** Clears the current image and crop (resets the component). */
    async clear() {
        this.setView("noPictureView");
    }
    componentWillLoad() {
        this.mergeResx();
    }
    componentDidLoad() {
        requestAnimationFrame(() => {
            this.setView("noPictureView");
        });
    }
    resxChanged() {
        this.mergeResx();
    }
    formResetCallback() {
        this.clear();
        this.internals.setValidity({});
        this.internals.setFormValue("");
    }
    mergeResx() {
        this.localResx = Object.assign(Object.assign({}, this.defaultResx), this.resx);
    }
    setView(newView) {
        const views = this.host.shadowRoot.querySelectorAll(".view");
        views.forEach(v => v.classList.remove("visible"));
        switch (newView) {
            case "noPictureView":
                this.noPictureView.classList.add("visible");
                break;
            case "hasPictureView":
                this.hasPictureView.classList.add("visible");
                break;
            default:
                break;
        }
        this.view = newView;
    }
    initCrop() {
        /** Force full size to prevent a Firefox timing issue */
        this.crop.style.top = "0px";
        this.crop.style.left = "0px";
        this.crop.style.width = "100%";
        this.crop.style.height = "100%";
        if (this.width == null || this.height == null) {
            return;
        }
        const wantedRatio = this.width / this.height;
        const imageRect = this.image.getBoundingClientRect();
        const imageRatio = imageRect.width / imageRect.height;
        if (wantedRatio > imageRatio) {
            const wantedHeight = imageRect.width / wantedRatio;
            const diff = imageRect.height - wantedHeight;
            this.crop.style.top = `${Math.round(diff / 2)}px`;
            this.crop.style.height = `${Math.round(wantedHeight)}px`;
        }
        else {
            const wantedWidth = imageRect.height * wantedRatio;
            const diff = imageRect.width - wantedWidth;
            this.crop.style.left = `${Math.round(diff / 2)}px`;
            this.crop.style.width = `${Math.round(wantedWidth)}px`;
        }
    }
    setImage() {
        this.image.addEventListener('load', () => {
            this.initCrop();
            this.emitImage();
        });
        this.image.src = this.canvas.toDataURL();
    }
    handleNewFile(file) {
        if (file.type.split('/')[0] != "image") {
            return;
        }
        this.fileName = file.name;
        var reader = new FileReader();
        reader.onload = readerLoadEvent => {
            var img = new Image();
            img.onload = () => {
                this.canvas.width = img.width;
                this.canvas.height = img.height;
                if (this.preventUndersized && this.width != undefined && this.height != undefined && (img.width < this.width || img.height < this.height)) {
                    this.imageTooSmallModal.show();
                    return;
                }
                var ctx = this.canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                requestAnimationFrame(() => {
                    this.setView("hasPictureView");
                });
                requestAnimationFrame(() => {
                    this.setImage();
                });
                requestAnimationFrame(() => {
                    this.initializeCropBox();
                });
            };
            img.src = readerLoadEvent.target.result.toString();
        };
        reader.readAsDataURL(file);
    }
    emitImage() {
        var _a, _b;
        var x = this.crop.offsetLeft / this.image.width * this.image.naturalWidth;
        var y = this.crop.offsetTop / this.image.height * this.image.naturalHeight;
        var cropRect = this.crop.getBoundingClientRect();
        var width = cropRect.width / this.image.width * this.image.naturalWidth;
        var height = cropRect.height / this.image.height * this.image.naturalHeight;
        if (x < 0)
            x = 0;
        if (x > this.image.naturalWidth)
            x = this.image.naturalWidth;
        if (y < 0)
            y = 0;
        if (y > this.image.naturalWidth)
            y = this.image.naturalWidth;
        if (width > this.image.naturalWidth)
            width = this.image.naturalWidth;
        if (height > this.image.naturalHeight)
            height = this.image.naturalHeight;
        var dataUrl = this.generateCroppedImage(x, y, width, height, (_a = this.width) !== null && _a !== void 0 ? _a : width, (_b = this.height) !== null && _b !== void 0 ? _b : height);
        this.imageCropChanged.emit(dataUrl);
        var file = dataURLtoFile(dataUrl, this.fileName);
        this.imageFileCropChanged.emit(file);
        if (this.name != undefined) {
            var data = new FormData();
            data.append(this.name, file);
            this.internals.setFormValue(data);
        }
    }
    generateCroppedImage(x, y, width, height, desiredWidth, desiredHeight) {
        this.canvas.width = desiredWidth;
        this.canvas.height = desiredHeight;
        const context = this.canvas.getContext("2d");
        context.clearRect(0, 0, desiredWidth, desiredHeight);
        context.drawImage(this.image, x, y, width, height, 0, 0, desiredWidth, desiredHeight);
        return this.canvas.toDataURL("image/jpeg", this.quality);
    }
    isMouseStillInTarget(event) {
        var inside = false;
        let mouseX = 0;
        let mouseY = 0;
        const imageRect = this.image.getBoundingClientRect();
        if (event instanceof MouseEvent) {
            mouseX = event.clientX;
            mouseY = event.clientY;
        }
        if (typeof TouchEvent !== "undefined") {
            if (event instanceof TouchEvent) {
                var touch = event.touches[0];
                mouseX = touch.clientX;
                mouseY = touch.clientY;
            }
        }
        if (mouseX >= imageRect.x &&
            mouseY >= imageRect.y &&
            mouseX <= imageRect.left + imageRect.width &&
            mouseY <= imageRect.top + imageRect.height) {
            inside = true;
        }
        var corners = this.crop.querySelectorAll("div");
        corners.forEach(corner => {
            var cornerRect = corner.getBoundingClientRect();
            if (mouseX >= cornerRect.x &&
                mouseY >= cornerRect.y &&
                mouseX <= cornerRect.left + cornerRect.width &&
                mouseY <= cornerRect.top + cornerRect.height) {
                inside = true;
            }
        });
        return inside;
    }
    render() {
        var _a, _b;
        return (h(Host, { key: 'af6c2df1c0df146d783f4616fec7da29468eaa04', tabIndex: this.focused ? -1 : 0, onFocus: () => this.inputField.focus(), onBlur: () => this.inputField.blur() }, h("canvas", { key: '7af554950d5b58e3efaee38c624ed24939407937', ref: el => this.canvas = el }), h("div", { key: '7a09006b6790eb3934e881ebe109a9b3dd2750f9', class: "view", ref: el => this.hasPictureView = el }, h("div", { key: '5c116796354c0e2bd3dd6d69593fc40898a78fc9', class: "cropper" }, h("img", { key: '87f72f7b69e01337d71e4291055a9ffbca5f4c96', ref: el => this.image = el, alt: this.fileName }), h("div", { key: 'c24a87d142bfab627f8ed0d9647a2b73efe76954', class: "backdrop" }), h("div", { key: '066f4e30840fea38819be154bb0833703467205a', class: "crop", ref: e => this.crop = e, onMouseDown: this.handleCropMouseDown, onTouchStart: this.handleCropMouseDown }, h("div", { key: 'bc8cdcee09201ca61ead012f58e91df58dc1bb10', class: "nw" }), h("div", { key: 'dacc389bbdb9a54439400af6eb6fd285fd5d4de2', class: "ne" }), h("div", { key: 'a2b7edb579cd3a9cfe2f44557a8dd7447ca45fd0', class: "se" }), h("div", { key: 'e1381b9569a19d70450f8e344ffffc3fa7c6ec39', class: "sw" })))), h("div", { key: '0a1e8be2893e8c09bff591169122a46754c2a12a', class: "view", ref: el => this.noPictureView = el }, h("dnn-dropzone", { key: 'fb134de05e5fcff750f7444f903ee9f542f4ee4b', allowCameraMode: true, onFilesSelected: e => this.handleNewFile(e.detail[0]), allowedExtensions: ['jpg', 'jpeg', 'gif', 'png', 'svg', 'webp', 'bmp'], resx: {
                capture: this.localResx.capture,
                dragAndDropFile: this.localResx.dragAndDropFile,
                or: this.localResx.or,
                takePicture: this.localResx.takePicture,
                uploadFile: this.localResx.uploadFile,
                uploadSizeTooLarge: "The file you tried to upload is too large.",
                fileSizeLimit: "The maximum size is",
            }, ref: el => this.inputField = el, onFocus: () => this.focused = true, onBlur: () => this.focused = false })), h("dnn-modal", { key: 'c9b9f9820d574c0272224fbd7dc2ba7ef178b1b7', ref: el => this.imageTooSmallModal = el, "close-text": this.localResx.modalCloseText }, h("p", { key: 'f9bb9c355af33f26d295f76067c60194b2f4532e' }, this.localResx.imageTooSmall.replace("{width}", ((_a = this.width) === null || _a === void 0 ? void 0 : _a.toString()) || "").replace("{height}", ((_b = this.height) === null || _b === void 0 ? void 0 : _b.toString()) || "")))));
    }
    static get is() { return "dnn-image-cropper"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-image-cropper.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-image-cropper.css"]
        };
    }
    static get properties() {
        return {
            "width": {
                "type": "number",
                "attribute": "width",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Sets the desired final image width."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "height": {
                "type": "number",
                "attribute": "height",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Sets the desired final image height."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "resx": {
                "type": "unknown",
                "attribute": "resx",
                "mutable": false,
                "complexType": {
                    "original": "ImageCropperResx",
                    "resolved": "ImageCropperResx | undefined",
                    "references": {
                        "ImageCropperResx": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/dnn-image-cropper/types.ts::ImageCropperResx"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Can be used to customize controls text.\nSome values support tokens, see default values for examples."
                },
                "getter": false,
                "setter": false
            },
            "quality": {
                "type": "number",
                "attribute": "quality",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the output quality of the cropped image (number between 0 and 1)."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "0.8"
            },
            "preventUndersized": {
                "type": "boolean",
                "attribute": "prevent-undersized",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When set to true, prevents cropping an image smaller than the required size, which would blow pixel and make the final picture look blurry."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name of the control when used in a form."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "view": {},
            "localResx": {},
            "fileName": {},
            "focused": {}
        };
    }
    static get events() {
        return [{
                "method": "imageCropChanged",
                "name": "imageCropChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "When the image crop changes, emits the dataurl for the new cropped image."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "imageFileCropChanged",
                "name": "imageFileCropChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emits both when a file is initially select and when the crop has changed.\nCompared to imageCropChanged, this event emits the file itself, which can be useful for uploading the file to a server including its name."
                },
                "complexType": {
                    "original": "File",
                    "resolved": "File",
                    "references": {
                        "File": {
                            "location": "global",
                            "id": "global::File"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "clear": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Clears the current image and crop (resets the component).",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "resx",
                "methodName": "resxChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=dnn-image-cropper.js.map
